import React, { useEffect, useRef, useState } from 'react';
import { Table, message } from 'antd';
import cloneDeep from 'lodash/cloneDeep';
import { Button, Dropdown, Input, Toggle } from '@quyva/shared-ui';
import { faAdd, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import { isValidNamingRegex } from '@quyva/common';

const FIELD_TYPES = ['text', 'integer', 'select'];

// Internal superset representation of a field. We keep every property in state
// and serialize selectively (see buildConfig) so toggling type/required never
// loses previously entered data.
const makeBlankField = () => ({
    label: '',
    type: 'text',
    is_required: false,
    default_value: '',
    is_big_text: false,
    select_options: [],
});

// Map a serialized field (from the stored config) onto the internal superset.
const normalizeField = (field) => {
    const type = FIELD_TYPES.includes(field?.type) ? field.type : 'text';
    return {
        label: field?.label ?? '',
        type,
        is_required: !!field?.is_required,
        default_value:
            field?.default_value ?? (type === 'integer' ? 0 : ''),
        is_big_text: !!field?.is_big_text,
        select_options: Array.isArray(field?.select_options)
            ? field.select_options
            : [],
    };
};

// Serialize the internal fields into the exact required JSON shape, omitting
// keys that do not apply to a field's type/required state.
const buildConfig = (useDetailedView, fields) => {
    const serializedFields = fields.map((f) => {
        const out = { label: f.label, type: f.type, is_required: f.is_required };
        if (f.type === 'select') {
            out.select_options = f.select_options;
        } else {
            if (f.type === 'text') {
                out.is_big_text = f.is_big_text;
            }
            if (!f.is_required) {
                out.default_value =
                    f.type === 'integer'
                        ? Number.isFinite(Number(f.default_value))
                            ? Number(f.default_value)
                            : 0
                        : f.default_value ?? '';
            }
        }
        return out;
    });
    return { use_detailed_view: useDetailedView, fields: serializedFields };
};

/**
 * The Form Designer. Mirrors the Template Designer (antd Table of fields +
 * shared-ui inputs/toggles/dropdowns). Field configuration follows the dynamic
 * visibility rules:
 *  - Default Value shows only when the field is NOT required (and type != select).
 *  - Big Text toggle shows only when type is text.
 *  - Select Options editor shows only when type is select.
 *  - Changing type resets the default value (0 for integer, "" for text).
 */
const FormDesigner = ({
    activeFormRecord,
    onFormNameChange,
    onFormConfigChange,
    onDirtyChange,
}) => {
    const [formName, setFormName] = useState('');
    const [useDetailedView, setUseDetailedView] = useState(false);
    const [fields, setFields] = useState([makeBlankField()]);
    const [selectedRows, setSelectedRows] = useState([]);
    // Snapshot of the form as loaded, used to detect unsaved changes.
    const baselineRef = useRef({ name: '', config: '' });

    // Load the active form (edit) or reset to a blank form (create).
    useEffect(() => {
        let nextName;
        let nextDetailedView;
        let nextFields;
        if (activeFormRecord) {
            let parsed = {};
            try {
                parsed = JSON.parse(activeFormRecord.config || '{}');
            } catch (e) {
                parsed = {};
            }
            nextName = activeFormRecord.form_name || '';
            nextDetailedView = !!parsed.use_detailed_view;
            const loadedFields = Array.isArray(parsed.fields)
                ? parsed.fields.map(normalizeField)
                : [];
            nextFields = loadedFields.length
                ? loadedFields
                : [makeBlankField()];
        } else {
            nextName = '';
            nextDetailedView = false;
            nextFields = [makeBlankField()];
        }
        setFormName(nextName);
        setUseDetailedView(nextDetailedView);
        setFields(nextFields);
        setSelectedRows([]);
        // Record the loaded snapshot so we only enable Save once it changes.
        baselineRef.current = {
            name: nextName,
            config: JSON.stringify(buildConfig(nextDetailedView, nextFields)),
        };
        if (onDirtyChange) onDirtyChange(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFormRecord]);

    // Lift the current name + serialized config up to the parent for saving,
    // and report whether the form differs from its loaded baseline.
    useEffect(() => {
        const currentConfig = JSON.stringify(
            buildConfig(useDetailedView, fields)
        );
        onFormNameChange(formName);
        onFormConfigChange(currentConfig);
        if (onDirtyChange) {
            const isDirty =
                formName !== baselineRef.current.name ||
                currentConfig !== baselineRef.current.config;
            onDirtyChange(isDirty);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formName, useDetailedView, fields]);

    const updateField = (index, mutator) => {
        setFields((prev) => {
            const next = cloneDeep(prev);
            mutator(next[index]);
            return next;
        });
    };

    const handleNameChange = (e) => {
        const value = e.target.value;
        if (value && !isValidNamingRegex(value, 0, 32)) {
            message.error(
                'Form name can only contain letters, numbers, and underscores, and must be less than 32 characters.'
            );
            return;
        }
        if (value.toLowerCase().startsWith('default')) {
            message.error('Form name cannot start with "default".');
            return;
        }
        setFormName(value);
    };

    const handleTypeChange = (index, newType) => {
        updateField(index, (field) => {
            field.type = newType;
            // Reset default value per type rules.
            field.default_value = newType === 'integer' ? 0 : '';
            if (newType !== 'text') field.is_big_text = false;
            if (newType !== 'select') field.select_options = [];
        });
    };

    const addField = () => {
        setFields((prev) => [...prev, makeBlankField()]);
    };

    const deleteSelectedFields = () => {
        setFields((prev) =>
            prev.filter((_, index) => !selectedRows.includes(index))
        );
        setSelectedRows([]);
    };

    const columns = [
        {
            title: 'Label',
            dataIndex: 'label',
            key: 'label',
            align: 'center',
            width: '24%',
            render: (text, record, index) => (
                <Input
                    value={text}
                    placeholder={`Field ${index + 1}`}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) =>
                        updateField(index, (f) => {
                            f.label = e.target.value;
                        })
                    }
                    hoverText='The label shown to the end-user for this field.'
                />
            ),
        },
        {
            title: 'Field Type',
            dataIndex: 'type',
            key: 'type',
            align: 'center',
            width: '16%',
            render: (type, record, index) => (
                <div className='w-full flex justify-center'>
                    <Dropdown
                        dropDownList={FIELD_TYPES.map((t) => ({
                            label: t.charAt(0).toUpperCase() + t.slice(1),
                            action: () => handleTypeChange(index, t),
                            selected: type === t,
                        }))}
                        showButtonText={true}
                        buttonText={type.charAt(0).toUpperCase() + type.slice(1)}
                        buttonHoverText='Choose the input type for this field.'
                    />
                </div>
            ),
        },
        {
            title: 'Required',
            dataIndex: 'is_required',
            key: 'is_required',
            align: 'center',
            width: '12%',
            render: (isRequired, record, index) => (
                <div className='w-full flex justify-center'>
                    <Toggle
                        checked={isRequired}
                        onChange={(checked) =>
                            updateField(index, (f) => {
                                f.is_required = checked;
                            })
                        }
                        title='If required, the end-user must fill this field (no default value).'
                    />
                </div>
            ),
        },
        {
            title: 'Default Value',
            dataIndex: 'default_value',
            key: 'default_value',
            align: 'center',
            width: '24%',
            render: (defaultValue, record, index) => {
                if (record.is_required || record.type === 'select') {
                    return (
                        <span className='text-texts-secondary300 text-sm'>
                            —
                        </span>
                    );
                }
                if (record.type === 'integer') {
                    return (
                        <Input
                            type='number'
                            value={defaultValue}
                            placeholder='0'
                            onChange={(e) =>
                                updateField(index, (f) => {
                                    const n = parseInt(e.target.value, 10);
                                    f.default_value = Number.isNaN(n) ? 0 : n;
                                })
                            }
                            hoverText='Default value used when the field is left empty.'
                        />
                    );
                }
                return (
                    <Input
                        value={defaultValue}
                        placeholder='Default text'
                        onChange={(e) =>
                            updateField(index, (f) => {
                                f.default_value = e.target.value;
                            })
                        }
                        hoverText='Default value used when the field is left empty.'
                    />
                );
            },
        },
        {
            title: 'Field Options',
            dataIndex: 'fieldOptions',
            key: 'fieldOptions',
            align: 'center',
            width: '24%',
            render: (_value, record, index) => {
                if (record.type === 'text') {
                    return (
                        <div className='w-full flex justify-center'>
                            <Toggle
                                toggleText='Big Text'
                                toggleTextClassName='mr-2 font-semibold text-texts-secondary300'
                                checked={record.is_big_text}
                                onChange={(checked) =>
                                    updateField(index, (f) => {
                                        f.is_big_text = checked;
                                    })
                                }
                                title='Render as a multi-line textarea instead of a single-line input.'
                            />
                        </div>
                    );
                }
                if (record.type === 'select') {
                    return (
                        <SelectOptionsEditor
                            options={record.select_options}
                            onChange={(opts) =>
                                updateField(index, (f) => {
                                    f.select_options = opts;
                                })
                            }
                        />
                    );
                }
                return (
                    <span className='text-texts-secondary300 text-sm'>—</span>
                );
            },
        },
    ];

    return (
        <div className='px-6 py-4'>
            {/* Form-level settings */}
            <div className='flex justify-between items-center mb-4'>
                <div className='flex items-center w-1/2 pr-6'>
                    <label className='mr-2 font-semibold text-texts-secondary300'>
                        Name
                    </label>
                    <Input
                        value={formName}
                        onChange={handleNameChange}
                        placeholder='Enter form name here'
                        hoverText='Select an appropriate name for the form.'
                    />
                </div>
                <div className='flex items-center'>
                    <Toggle
                        toggleText='Detailed View'
                        toggleTextClassName='mr-2 font-semibold text-texts-secondary300'
                        checked={useDetailedView}
                        onChange={setUseDetailedView}
                        title='Render the form in a detailed view layout.'
                    />
                </div>
            </div>

            <div className='border-t pt-4 border-gray-300'>
                <div className='flex justify-end mb-4'>
                    <Button
                        icon={faAdd}
                        onClick={addField}
                        text={'Add Field'}
                        type={'success'}
                        disabled={!formName}
                        hoverText={
                            !formName
                                ? 'Please enter a form name to continue'
                                : 'Add a new field'
                        }
                    />
                    <Button
                        onClick={deleteSelectedFields}
                        text={'Delete Field(s)'}
                        type={'danger'}
                        icon={faTrash}
                        disabled={selectedRows.length === 0}
                        className='ml-2'
                        hoverText={'Delete 1 or more fields.'}
                    />
                </div>

                <div className='overflow-x-auto'>
                    <Table
                        rowSelection={{
                            type: 'checkbox',
                            selectedRowKeys: selectedRows,
                            onChange: (selectedRowKeys) =>
                                setSelectedRows(selectedRowKeys),
                        }}
                        columns={columns}
                        dataSource={fields}
                        rowKey={(record, index) => index}
                        pagination={fields.length > 10 ? { pageSize: 10 } : false}
                        bordered
                        size='small'
                        scroll={{ y: '50vh' }}
                    />
                </div>
            </div>
        </div>
    );
};

// Dynamic add/edit/remove list for a select field's options.
const SelectOptionsEditor = ({ options, onChange }) => {
    return (
        <div className='flex flex-col space-y-2'>
            {options.map((opt, i) => (
                <div key={i} className='flex items-center space-x-2'>
                    <Input
                        value={opt}
                        placeholder={`Option ${i + 1}`}
                        onChange={(e) => {
                            const next = options.map((o, idx) =>
                                idx === i ? e.target.value : o
                            );
                            onChange(next);
                        }}
                    />
                    <Button
                        onClick={() =>
                            onChange(options.filter((_, idx) => idx !== i))
                        }
                        icon={faXmark}
                        type={'danger'}
                        className='!border-0 !shadow-none !bg-transparent w-10 h-10 text-lg p-2'
                    />
                </div>
            ))}
            <Button
                onClick={() => onChange([...options, ''])}
                icon={faAdd}
                type={'info'}
                text={'Add Option'}
                className='!border-0 !shadow-none !bg-transparent text-md p-2'
            />
        </div>
    );
};

export default FormDesigner;
